import { useEffect, useMemo, useRef, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';
import DownloadDialog from '../components/DownloadDialog';
import EditorSettingsDrawer from '../components/EditorSettingsDrawer';
import MoveDialog from '../components/MoveDialog';
import RichTextEditor from '../components/RichTextEditor';
import { useWorkspace } from '../store/WorkspaceContext';
import { DEFAULT_APPEARANCE } from '../utils/constants';
import { getFileDisplayName } from '../utils/workspace';

export default function EditorPage() {
  const { fileId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    files,
    setCurrentFolderId,
    renameFile,
    validateName,
    updateFile,
    updateFileAppearance,
    resetFileAppearanceToDefault,
    deleteFile,
    moveFile,
  } = useWorkspace();

  const file = files.find((item) => item.id === fileId);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState('Saved');
  const [titleDraft, setTitleDraft] = useState('');
  const [titleError, setTitleError] = useState('');
  const saveTimerRef = useRef(null);

  const appearance = useMemo(
    () => ({ ...DEFAULT_APPEARANCE, ...(file?.appearance || {}) }),
    [file?.appearance]
  );

  useEffect(() => {
    if (file?.id) {
      setCurrentFolderId(file.parentFolderId);
      setTitleDraft(file.name);
      setTitleError('');
    }
  }, [file?.id, file?.name, file?.parentFolderId, setCurrentFolderId]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  if (!file) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'background.default',
          p: 2,
          textAlign: 'center',
        }}
      >
        <Stack spacing={2} sx={{ alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800}>
            File not found
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Back to Explorer
          </Button>
        </Stack>
      </Box>
    );
  }

  const showSavingStatus = () => {
    setSaveStatus('Saving...');

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(() => setSaveStatus('Saved'), 450);
  };

  const handleDocumentChange = (documentPatch) => {
    updateFile(file.id, documentPatch);
    showSavingStatus();
  };

  const commitTitle = () => {
    const nextName = titleDraft.trim().replace(/\.txt$/i, '');

    if (!nextName || nextName === file.name) {
      setTitleDraft(file.name);
      setTitleError('');
      return;
    }

    const validationError = validateName({
      type: 'file',
      name: nextName,
      parentId: file.parentFolderId,
      excludeId: file.id,
    });

    if (validationError) {
      setTitleError(validationError);
      return;
    }

    try {
      renameFile(file.id, nextName);
      setTitleError('');
    } catch (error) {
      setTitleError(error.message || 'Unable to rename this file.');
    }
  };

  const controlBorder = alpha(appearance.textColor, 0.22);
  const controlSurface = alpha(appearance.textColor, 0.08);
  const iconButtonSx = {
    color: appearance.textColor,
    bgcolor: controlSurface,
    '&:hover': {
      bgcolor: alpha(appearance.textColor, 0.14),
    },
  };
  const titleFieldSx = {
    minWidth: { xs: 160, sm: 260 },
    '& .MuiInput-root': {
      color: appearance.textColor,
      fontWeight: 900,
      bgcolor: 'transparent',
    },
    '& .MuiInputBase-input::placeholder': {
      color: alpha(appearance.textColor, 0.62),
      opacity: 1,
    },
    '& .MuiFormHelperText-root': {
      color: '#ef4444',
    },
    '&:before': {
      borderBottomColor: controlBorder,
    },
    '&:hover:not(.Mui-disabled):before': {
      borderBottomColor: alpha(appearance.textColor, 0.42),
    },
  };

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        bgcolor: appearance.backgroundColor,
        color: appearance.textColor,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        component="header"
        sx={{
          flexShrink: 0,
          px: { xs: 1, sm: 2 },
          py: 1,
          borderBottom: 1,
          borderColor: alpha(appearance.textColor, 0.16),
          bgcolor: alpha(appearance.backgroundColor, theme.palette.mode === 'dark' ? 0.92 : 0.86),
          backdropFilter: 'blur(12px)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          sx={{ alignItems: { xs: 'stretch', md: 'center' } }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', minWidth: 0, flexGrow: 1 }}
          >
            <Tooltip title="Back">
              <IconButton
                aria-label="Back to explorer"
                onClick={() => navigate('/')}
                sx={iconButtonSx}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Box sx={{ minWidth: 0, maxWidth: { xs: '100%', sm: 420 } }}>
              <TextField
                variant="standard"
                value={titleDraft}
                error={Boolean(titleError)}
                helperText={titleError || ''}
                onChange={(event) => {
                  setTitleDraft(event.target.value);
                  if (titleError) {
                    setTitleError('');
                  }
                }}
                onBlur={commitTitle}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }

                  if (event.key === 'Escape') {
                    setTitleDraft(file.name);
                    setTitleError('');
                    event.currentTarget.blur();
                  }
                }}
                sx={titleFieldSx}
              />
              <Chip
                size="small"
                label={saveStatus}
                sx={{
                  mt: 0.5,
                  bgcolor: controlSurface,
                  color: appearance.textColor,
                }}
              />
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Tooltip title="Editor shortcuts">
              <IconButton
                aria-label="Editor shortcuts"
                onClick={() => setHelpOpen(true)}
                sx={iconButtonSx}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton
                aria-label="Download file"
                onClick={() => setDownloadOpen(true)}
                sx={iconButtonSx}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Settings">
              <IconButton
                aria-label="Open settings"
                onClick={() => setDrawerOpen(true)}
                sx={iconButtonSx}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <RichTextEditor
          file={file}
          appearance={appearance}
          onDocumentChange={handleDocumentChange}
        />
      </Box>

      <EditorSettingsDrawer
        open={drawerOpen}
        file={file}
        onClose={() => setDrawerOpen(false)}
        onAppearanceChange={(appearancePatch) => updateFileAppearance(file.id, appearancePatch)}
        onAppearanceReset={() =>
          resetFileAppearanceToDefault(file.id)
        }
        onMove={() => {
          setDrawerOpen(false);
          setMoveOpen(true);
        }}
        onDelete={() => {
          setDrawerOpen(false);
          setDeleteOpen(true);
        }}
        onDownload={() => {
          setDrawerOpen(false);
          setDownloadOpen(true);
        }}
      />

      <MoveDialog
        open={moveOpen}
        item={file}
        itemType="file"
        currentParentId={file.parentFolderId}
        onMove={(destinationId) => moveFile(file.id, destinationId)}
        onClose={() => setMoveOpen(false)}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete File"
        description={`Delete ${getFileDisplayName(file)}? This cannot be undone.`}
        onConfirm={() => {
          deleteFile(file.id);
          navigate('/');
        }}
        onClose={() => setDeleteOpen(false)}
      />

      <DownloadDialog
        open={downloadOpen}
        item={file}
        itemType="file"
        onClose={() => setDownloadOpen(false)}
      />

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Editor Shortcuts</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            {[
              ['Cmd/Ctrl + B', 'Bold'],
              ['Cmd/Ctrl + I', 'Italic'],
              ['Cmd/Ctrl + U', 'Underline'],
              ['Cmd/Ctrl + Z', 'Undo'],
              ['Cmd/Ctrl + Shift + Z', 'Redo'],
              ['Ctrl + Y', 'Redo on Windows'],
            ].map(([shortcut, label]) => (
              <Stack
                key={shortcut}
                direction="row"
                sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
              >
                <Typography variant="body2" fontWeight={800}>
                  {shortcut}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
